import { supabase } from '@/integrations/supabase/client';

export interface GuestContact {
  id: string;
  user_id: string;
  name: string;
  email?: string;
  phone?: string;
  tags?: string[];
  created_at: string;
  updated_at: string;
}

export interface CreateGuestContactData {
  name: string;
  email?: string;
  phone?: string;
  tags?: string[];
}

export const guestContactService = {
  async getUserGuestContacts(): Promise<GuestContact[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('guest_contacts')
      .select('*')
      .eq('user_id', user.id)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async createGuestContact(contactData: CreateGuestContactData): Promise<GuestContact> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('guest_contacts')
      .insert({
        ...contactData,
        user_id: user.id,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateGuestContact(id: string, updates: Partial<CreateGuestContactData>): Promise<GuestContact> {
    const { data, error } = await supabase
      .from('guest_contacts')
      .update(updates)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteGuestContact(id: string): Promise<void> {
    const { error } = await supabase
      .from('guest_contacts')
      .delete()
      .eq('id', id);

    if (error) throw error;
  },

  async searchGuestContacts(query: string): Promise<GuestContact[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('guest_contacts')
      .select('*')
      .eq('user_id', user.id)
      .or(`name.ilike.%${query}%,email.ilike.%${query}%`)
      .order('name');

    if (error) throw error;
    return data || [];
  },

  async getGuestContactsByTags(tags: string[]): Promise<GuestContact[]> {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data, error } = await supabase
      .from('guest_contacts')
      .select('*')
      .eq('user_id', user.id)
      .overlaps('tags', tags)
      .order('name');

    if (error) throw error;
    return data || [];
  }
};